import { Field, UseFieldConfig } from 'react-final-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRefState } from '@bay-email/editor';
import { debounce } from 'lodash';
import { Form, FormItemProps } from '@arco-design/web-react';

export interface EnhancerProps {
	name: string;
	onChangeAdapter?: (value: any) => any;
	validate?: (value: any) => string | undefined | Promise<string | undefined>;
	config?: UseFieldConfig<any, any>;
	changeOnBlur?: boolean;
	formItem?: FormItemProps;
	label?: FormItemProps['label'];
	inline?: boolean;
	equalSpacing?: boolean;
	required?: boolean;
	autoComplete?: 'on' | 'off';
	style?: React.CSSProperties;
	helpText?: React.ReactNode;
	debounceTime?: number;
	labelHidden?: boolean;
	secondaryLabel?: boolean;
	/**
	 * Optional node rendered at the right edge of the label row, inline with the
	 * label (e.g. a "Dynamic" pill). Receives the current field value so it can react
	 * to it. Returning a falsy value renders nothing.
	 */
	renderLabelSuffix?: (value: any) => React.ReactNode;
}

const parse = (v: any) => v;
export default function enhancer<P extends { onChange?: (...rest: any) => any }>(
	Component: React.FC<any>,
	changeAdapter: (args: Parameters<NonNullable<P['onChange']>>) => any,
	option?: { debounceTime: number },
) {
	return (props: EnhancerProps & Omit<P, 'value' | 'onChange' | 'mutators'>) => {
		const {
			name,
			validate,
			onChangeAdapter,
			changeOnBlur,
			inline,
			equalSpacing,
			formItem,
			label,
			required,
			style,
			helpText,
			autoComplete,
			labelHidden,
			secondaryLabel,
			renderLabelSuffix,
			...rest
		} = props;

		const debounceTime = props.debounceTime || option?.debounceTime || 300;

		const config = useMemo(() => {
			return {
				...props.config,
				validate: validate,
				parse: props.config?.parse || parse,
			};
		}, [props.config, validate]);

		const [currentValue, setCurrentValue] = useState(undefined);
		const currentValueRef = useRefState(currentValue);

		const layoutStyle = useMemo((): FormItemProps => {
			if (equalSpacing) {
				return {
					labelCol: {
						span: 11,
						style: {
							textAlign: 'left',
							paddingRight: 0,
						},
					},
					wrapperCol: {
						span: 11,
						offset: 1,
						style: {
							textAlign: 'right',
						},
					},
				};
			}
			if (inline) {
				return {
					labelCol: {
						span: 7,
						style: {
							textAlign: 'right',
							paddingRight: 0,
						},
					},
					wrapperCol: {
						span: 16,
						offset: 1,
						style: {},
					},
				};
			}

			return {
				labelCol: {
					span: 24,
					style: {
						paddingRight: 0,
						paddingLeft: 0,
					},
				},
				wrapperCol: {
					span: 24,
					style: {
						paddingLeft: 0,
					},
				},
			};
		}, [equalSpacing, inline]);

		return useMemo(() => {
			return (
				<Field
					name={name}
					{...config}
				>
					{({ input: { onBlur, onChange, value }, meta }) => {
						// eslint-disable-next-line react-hooks/exhaustive-deps

						const debounceCallbackChange = useCallback(
							debounce(
								val => {
									onChange(val);
									onBlur();
								},
								debounceTime,
								{
									// maxWait: 500,
								},
							),
							[onChange, onBlur],
						);

						const onFieldChange: P['onChange'] = useCallback(
							(e: any) => {
								const newVal = onChangeAdapter
									? onChangeAdapter(changeAdapter(e))
									: changeAdapter(e);

								setCurrentValue(newVal);
								if (!changeOnBlur) {
									debounceCallbackChange(newVal);
								}
							},
							[debounceCallbackChange],
						);

						const onFieldBlur = useCallback(() => {
							if (changeOnBlur) {
								onChange(currentValueRef.current);
								onBlur();
							}
						}, [onBlur, onChange]);

						useEffect(() => {
							// Convert empty string to undefined to allow placeholder to show
							setCurrentValue(value === '' ? undefined : value);
						}, [value]);

						// Capitalize and style the label, but preserve lowercase for units in parentheses
						const labelText = label || formItem?.label || '';
						const baseLabel = labelHidden
							? undefined
							: (() => {
									// Only process string labels - if it's a React element or other type, use it directly
									if (typeof labelText !== 'string') {
										return labelText;
									}

									const hasUnitSuffix = /\([a-z%]+\)/i.test(labelText);

									// For string labels, match patterns like "(px)", "(em)", "(rem)", "(%)" and preserve them as lowercase
									const parts = labelText.split(/(\([a-z%]+\))/gi);
									return (
										<span
											style={{
												fontWeight: secondaryLabel ? 400 : 600,
												color: secondaryLabel ? 'rgba(109, 113, 117, 1)' : '#000000',
												whiteSpace: hasUnitSuffix ? 'nowrap' : undefined,
											}}
										>
											{parts.map((part, index) => {
												// If part matches unit pattern (px), (em), etc., keep it lowercase
												if (/^\([a-z%]+\)$/i.test(part)) {
													return (
														<span
															key={index}
															style={{ textTransform: 'lowercase' }}
														>
															{part}
														</span>
													);
												}
												return (
													<span
														key={index}
														style={{ textTransform: 'capitalize' }}
													>
														{part}
													</span>
												);
											})}
										</span>
									);
								})();

						// When a label suffix is provided (e.g. a "Dynamic" pill), render it at
						// the right edge of the label row, inline with the label.
						const labelSuffix = !labelHidden && renderLabelSuffix
							? renderLabelSuffix(value)
							: null;
						const formattedLabel = labelSuffix
							? (
								<span
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										width: '100%',
										gap: '0.5rem',
									}}
								>
									{baseLabel}
									{labelSuffix}
								</span>
							)
							: baseLabel;

						// Extract marginBottom from formItem.style if it exists, otherwise default to 12px
						const formItemMarginBottom = formItem?.style?.marginBottom ?? '12px';
						const { marginBottom: _, ...formItemStyleWithoutMargin } =
							formItem?.style || {};

						return (
							<Form.Item
								style={{
									...style,
									marginTop: '0px',
									marginLeft: '0px',
									marginRight: '0px',
									marginBottom: formItemMarginBottom,
									...formItemStyleWithoutMargin,
								}}
								rules={required ? [{ required: true }] : undefined}
								{...layoutStyle}
								{...(formItem ? { ...formItem, style: undefined } : {})}
								label={formattedLabel}
								labelAlign='left'
								validateStatus={meta.touched && meta.error ? 'error' : undefined}
								help={meta.touched && meta.error ? meta.error : helpText}
							>
								<div
									onMouseOverCapture={event => {
										const target = event.target as HTMLElement | null;
										if (!target) {
											return;
										}

										if (target.hasAttribute('title')) {
											target.removeAttribute('title');
										}

										target.querySelectorAll('[title]').forEach(node => {
											node.removeAttribute('title');
										});

										const nearestTitledParent = target.closest(
											'[title]',
										) as HTMLElement | null;
										if (nearestTitledParent) {
											nearestTitledParent.removeAttribute('title');
										}
									}}
									onFocusCapture={event => {
										const target = event.target as HTMLElement | null;
										if (!target) {
											return;
										}

										if (target.hasAttribute('title')) {
											target.removeAttribute('title');
										}

										target.querySelectorAll('[title]').forEach(node => {
											node.removeAttribute('title');
										});

										const nearestTitledParent = target.closest(
											'[title]',
										) as HTMLElement | null;
										if (nearestTitledParent) {
											nearestTitledParent.removeAttribute('title');
										}
									}}
								>
									<Component
										autoComplete={autoComplete}
										{...rest}
										name={name}
										checked={currentValue}
										value={currentValue || undefined}
										onChange={onFieldChange}
										onBlur={onFieldBlur}
									/>
								</div>
							</Form.Item>
						);
					}}
				</Field>
			);
		}, [
			autoComplete,
			changeOnBlur,
			config,
			currentValue,
			currentValueRef,
			debounceTime,
			formItem,
			helpText,
			label,
			labelHidden,
			layoutStyle,
			name,
			onChangeAdapter,
			required,
			rest,
			secondaryLabel,
			style,
		]);
	};
}
